import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, ArrowRight, Star, Clock, ShieldCheck, CalendarCheck } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ServiceCard from "../components/ServiceCard";
import { API } from "../context/AuthContext";
import { useSeo } from "../lib/seo";
import { fetchCategories, visibleCategories } from "../lib/categories";
import { categoryImage, HERO_COLLAGE } from "../lib/categoryImages";
import {
  BOOKFREELY_SHORT_DESCRIPTION,
  HOW_IT_WORKS_CUSTOMERS,
  HOW_IT_WORKS_BUSINESSES,
} from "../lib/brand";

export default function Landing() {
  const [query, setQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const nav = useNavigate();

  useSeo({
    title: "BookFreely — Online marketplace for local service businesses",
    description:
      "Discover, compare, and book local service businesses on BookFreely. No customer subscription. A one-time booking and processing fee of 3.85% of the full service price is charged at checkout.",
    path: "/",
  });

  useEffect(() => { fetchCategories().then(setCategories); }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-bf-ld", "organization");
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "BookFreely",
      url: "https://bookfreely.co",
      logo: "https://bookfreely.co/icon-512.png?v=2",
      image: "https://bookfreely.co/icon-512.png?v=2",
      description:
        "BookFreely is an online marketplace that helps customers discover and book local service businesses. Businesses can list services, manage appointments, and accept online bookings through one platform.",
      foundingDate: "2026",
      sameAs: [
        "https://www.instagram.com/bookfreely",
        "https://www.tiktok.com/@bookfreely_official",
      ],
      contactPoint: {
        "@type": "ContactPoint",
        email: "bookfreely.support@gmail.com",
        contactType: "customer support",
        availableLanguage: ["English"],
      },
    });
    document.head.appendChild(script);
    return () => {
      const existing = document.head.querySelector('script[data-bf-ld="organization"]');
      if (existing) existing.remove();
    };
  }, []);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    nav(`/services?q=${encodeURIComponent(query)}`);
  };

  const cats = visibleCategories(categories);

  return (
    <div className="App" style={{ background: "var(--gf-bg)" }}>
      <Header hideSearch />

      {/* ───────── HERO ───────── */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-12 sm:pt-20 pb-10 sm:pb-16">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center">
          <div>
            <div className="fade-up d1 flex items-center gap-2 text-xs font-medium" style={{ color: "var(--gf-text-muted)" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--gf-success)" }} />
              No customer subscription · Book It Freely.
            </div>
            <h1 className="fade-up d2 font-display text-[2.6rem] leading-[1.03] sm:text-6xl lg:text-[4.25rem] mt-4 tracking-tight" style={{ color: "var(--gf-text)" }}>
              Find and book<br />great local services.
            </h1>
            <p className="fade-up d3 text-base sm:text-lg mt-5 max-w-xl leading-relaxed" style={{ color: "var(--gf-text-soft)" }}>
              Discover trusted local businesses, see real prices, reviews and availability, and book online in seconds — with no customer subscription.
            </p>

            <form onSubmit={submit} className="fade-up d4 mt-7 flex items-center gap-2 p-2 rounded-2xl max-w-xl"
              style={{ background: "var(--gf-search-bg)", border: "1px solid var(--gf-border-strong)", boxShadow: "var(--gf-shadow-ambient)" }}>
              <div className="flex-1 flex items-center gap-3 pl-3">
                <Search size={19} style={{ color: "var(--gf-text-muted)" }} />
                <input
                  data-testid="hero-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Try “barber”, “detailing”, “photographer”…"
                  className="bg-transparent outline-none flex-1 text-base py-2.5"
                  style={{ color: "var(--gf-text)" }}
                />
              </div>
              <button
                data-testid="hero-search-submit"
                type="submit"
                className="rounded-xl font-semibold px-5 sm:px-6 py-3 text-sm sm:text-base active:scale-[0.98] shrink-0"
                style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
              >
                <span className="hidden sm:inline">Search</span>
                <Search size={18} className="sm:hidden" />
              </button>
            </form>

            <div className="fade-up d5 mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" style={{ color: "var(--gf-text-muted)" }}>
              <span className="inline-flex items-center gap-1.5"><Star size={15} style={{ color: "var(--gf-star)" }} fill="var(--gf-star)" /> Reviews from completed bookings</span>
              <span className="inline-flex items-center gap-1.5"><CalendarCheck size={15} /> Instant & request booking</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={15} /> Secure Stripe payments</span>
            </div>
          </div>

          {/* Editorial collage — real craft photography, not boxed cards */}
          <div className="fade-up d3 hidden lg:grid grid-cols-5 grid-rows-6 gap-3 h-[30rem]">
            <div className="col-span-3 row-span-4 relative rounded-[22px] overflow-hidden bf-img-zoom-parent">
              <img src={HERO_COLLAGE[0].url} alt={HERO_COLLAGE[0].label} loading="eager" className="w-full h-full object-cover bf-img-zoom" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(15,23,42,0.55), transparent 55%)" }} />
              <div className="absolute left-4 bottom-4 text-white">
                <div className="font-display text-lg font-bold">{HERO_COLLAGE[0].label}</div>
              </div>
            </div>
            <div className="col-span-2 row-span-3 relative rounded-[22px] overflow-hidden bf-img-zoom-parent">
              <img src={HERO_COLLAGE[1].url} alt={HERO_COLLAGE[1].label} className="w-full h-full object-cover bf-img-zoom" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(15,23,42,0.5), transparent 60%)" }} />
              <div className="absolute left-3 bottom-3 text-white">
                <div className="font-display text-sm font-bold">{HERO_COLLAGE[1].label}</div>
              </div>
            </div>
            <div className="col-span-2 row-span-3 relative rounded-[22px] overflow-hidden bf-img-zoom-parent">
              <img src={HERO_COLLAGE[2].url} alt={HERO_COLLAGE[2].label} className="w-full h-full object-cover bf-img-zoom" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(15,23,42,0.5), transparent 60%)" }} />
              <div className="absolute left-3 bottom-3 text-white">
                <div className="font-display text-sm font-bold">{HERO_COLLAGE[2].label}</div>
              </div>
            </div>
            <div className="col-span-3 row-span-2 rounded-[22px] flex flex-col justify-center px-5"
              style={{ background: "var(--gf-surface)", border: "1px solid var(--gf-border)" }}>
              <div className="font-display text-3xl font-extrabold" style={{ color: "var(--gf-text)" }}>Book It Freely.</div>
              <div className="text-sm mt-0.5" style={{ color: "var(--gf-text-muted)" }}>One platform. Every local service.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── CATEGORIES (photo-led, horizontal scroll) ───────── */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-6 sm:pt-10">
        <div className="flex items-end justify-between mb-5">
          <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: "var(--gf-text)" }}>Browse by category</h2>
          <button data-testid="see-all-categories-btn" onClick={() => nav("/services")}
            className="text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all" style={{ color: "var(--gf-accent)" }}>
            All services <ArrowRight size={15} />
          </button>
        </div>
        <div className="flex gap-4 overflow-x-auto scrollbar-hide snap-x-mandatory pb-2 -mx-5 px-5 sm:mx-0 sm:px-0" data-testid="categories-strip">
          {cats.map((c, i) => (
            <button
              key={c.slug}
              data-testid={`category-${c.slug}`}
              onClick={() => nav(`/services?category=${encodeURIComponent(c.slug)}`)}
              className="snap-start shrink-0 w-40 sm:w-48 text-left group"
            >
              <div className="relative rounded-2xl overflow-hidden bf-img-zoom-parent" style={{ aspectRatio: "4/5", boxShadow: "var(--gf-shadow-subtle)" }}>
                <img src={categoryImage(c.slug)} alt={c.label} loading="lazy" className="w-full h-full object-cover bf-img-zoom" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(15,23,42,0.68), rgba(15,23,42,0.05) 55%)" }} />
                <div className="absolute left-3.5 right-3 bottom-3">
                  <div className="font-display text-white font-bold text-base leading-tight">{c.label}</div>
                  {typeof c.count === "number" && c.count > 0 && (
                    <div className="text-white/80 text-[11px] mt-0.5">{c.count} {c.count === 1 ? "service" : "services"}</div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ───────── CURATED / RECENTLY LISTED ───────── */}
      {services.length > 0 ? (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-14 sm:pt-20">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="uppercase text-[11px] tracking-[0.22em] font-semibold" style={{ color: "var(--gf-text-muted)" }}>Fresh on BookFreely</div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mt-1" style={{ color: "var(--gf-text)" }}>Recently listed services</h2>
            </div>
            <button data-testid="see-all-services-btn" onClick={() => nav("/services")}
              className="text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all" style={{ color: "var(--gf-accent)" }}>
              See all <ArrowRight size={15} />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => <ServiceCard key={s.id} s={s} />)}
          </div>
        </section>
      ) : (
        <section className="max-w-3xl mx-auto px-5 sm:px-6 pt-16 text-center" data-testid="landing-empty-services">
          <div className="uppercase text-[11px] tracking-[0.22em] font-semibold" style={{ color: "var(--gf-text-muted)" }}>Just getting started</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2" style={{ color: "var(--gf-text)" }}>We're onboarding the first wave of businesses.</h2>
          <p className="text-base mt-3" style={{ color: "var(--gf-text-soft)" }}>
            Be one of the first listings on BookFreely. Free trial, full control over deposits, automatic email reminders.
          </p>
          <button data-testid="empty-landing-cta" onClick={() => nav("/signup?role=business")}
            className="inline-flex items-center gap-2 mt-6 rounded-full px-6 py-3 text-sm font-semibold active:scale-[0.98]"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
            List your business <ArrowRight size={16} />
          </button>
        </section>
      )}

      {/* ───────── WHAT IS BOOKFREELY (airy, no box) ───────── */}
      <section className="max-w-3xl mx-auto px-5 sm:px-6 pt-20 sm:pt-28" data-testid="what-is-bookfreely">
        <div className="uppercase text-[11px] tracking-[0.22em] font-semibold" style={{ color: "var(--gf-text-muted)" }}>What is BookFreely?</div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2 leading-tight" style={{ color: "var(--gf-text)" }}>
          An online marketplace for local services.
        </h2>
        <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: "var(--gf-text-soft)" }} data-testid="bookfreely-description">
          {BOOKFREELY_SHORT_DESCRIPTION}
        </p>
        <button data-testid="learn-more-btn" onClick={() => nav("/about")}
          className="mt-5 text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all" style={{ color: "var(--gf-accent)" }}>
          Learn more about BookFreely <ArrowRight size={15} />
        </button>
      </section>

      {/* ───────── HOW IT WORKS (two columns, divider, no heavy boxes) ───────── */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-16 sm:pt-24" data-testid="how-it-works">
        <h2 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "var(--gf-text)" }}>How BookFreely works</h2>
        <div className="mt-8 grid lg:grid-cols-2 gap-x-14 gap-y-12">
          <div data-testid="how-it-works-customers-card">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] pb-4 mb-5 border-b" style={{ color: "var(--gf-text-muted)", borderColor: "var(--gf-border)" }}>For customers</div>
            <ol className="space-y-5">
              {HOW_IT_WORKS_CUSTOMERS.map((s) => (
                <li key={s.n} className="flex items-start gap-4">
                  <span className="font-display text-sm font-bold flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: "var(--gf-surface)", color: "var(--gf-text)" }}>{s.n}</span>
                  <div>
                    <div className="text-[15px] font-semibold" style={{ color: "var(--gf-text)" }}>{s.t}</div>
                    <div className="text-sm mt-0.5 leading-relaxed" style={{ color: "var(--gf-text-muted)" }}>{s.d}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div data-testid="how-it-works-businesses-card" className="lg:pl-14 lg:border-l" style={{ borderColor: "var(--gf-border)" }}>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] pb-4 mb-5 border-b" style={{ color: "var(--gf-text-muted)", borderColor: "var(--gf-border)" }}>For businesses</div>
            <ol className="space-y-5">
              {HOW_IT_WORKS_BUSINESSES.map((s) => (
                <li key={s.n} className="flex items-start gap-4">
                  <span className="font-display text-sm font-bold flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: "var(--gf-surface)", color: "var(--gf-text)" }}>{s.n}</span>
                  <div>
                    <div className="text-[15px] font-semibold" style={{ color: "var(--gf-text)" }}>{s.t}</div>
                    <div className="text-sm mt-0.5 leading-relaxed" style={{ color: "var(--gf-text-muted)" }}>{s.d}</div>
                  </div>
                </li>
              ))}
            </ol>
            <button data-testid="how-it-works-cta-signup" onClick={() => nav("/signup?role=business")}
              className="mt-7 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold active:scale-[0.98]"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              Get started as a business <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </section>

      {/* ───────── TRUST (inline, divider-led, no colored boxes) ───────── */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-16 sm:pt-24" data-testid="trust-strip">
        <div className="grid sm:grid-cols-3 gap-8 sm:gap-10">
          {[
            { icon: ShieldCheck, l: "Verified means Stripe-approved", s: "Businesses can list and take bookings once subscribed; only those Stripe has approved for payouts show the Verified check." },
            { icon: CalendarCheck, l: "Refundable deposits, labeled upfront", s: "Every deposit is clearly labeled refundable or non-refundable before you book." },
            { icon: Clock, l: "Reminders, right on time", s: "Automatic email reminders 24 hours and 1 hour before every appointment." },
          ].map((b, i) => (
            <div key={i} data-testid={`trust-item-${i}`}>
              <b.icon size={22} style={{ color: "var(--gf-accent)" }} />
              <div className="text-[15px] font-semibold mt-3" style={{ color: "var(--gf-text)" }}>{b.l}</div>
              <div className="text-sm mt-1 leading-relaxed" style={{ color: "var(--gf-text-muted)" }}>{b.s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ───────── BUSINESS CTA (one calm panel, allowed) ───────── */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 py-16 sm:py-24">
        <div className="rounded-[26px] overflow-hidden relative" style={{ background: "#0F172A", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "var(--gf-shadow-lifted)" }}>
          <div className="px-7 sm:px-14 py-12 sm:py-16 max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: "rgba(255,255,255,0.55)" }}>For business owners</div>
            <h2 className="font-display text-3xl sm:text-5xl font-extrabold mt-3 leading-[1.05]" style={{ color: "#fff" }}>
              Your professional storefront, booking & payments — in one place.
            </h2>
            <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.75)" }}>
              Set your own deposit, decide if it's refundable, and manage Instant Bookings and booking requests from your dashboard. Built-in calendar with automatic email reminders.
            </p>
            <div className="mt-7 flex flex-wrap gap-6" data-testid="landing-pricing-summary">
              <div><div className="font-display text-2xl font-bold" style={{ color: "#fff" }}>$19.99<span className="text-sm font-normal" style={{ color: "rgba(255,255,255,0.6)" }}>/mo</span></div><div className="text-[11px] uppercase tracking-[0.14em] mt-1" style={{ color: "rgba(255,255,255,0.85)" }}>7-day free trial</div></div>
              <div><div className="font-display text-2xl font-bold" style={{ color: "#fff" }}>$199.99<span className="text-sm font-normal" style={{ color: "rgba(255,255,255,0.6)" }}>/year</span></div><div className="text-[11px] uppercase tracking-[0.14em] mt-1" style={{ color: "rgba(255,255,255,0.5)" }}>billed annually</div></div>
              <div><div className="font-display text-2xl font-bold" style={{ color: "#fff" }}>0%</div><div className="text-[11px] uppercase tracking-[0.14em] mt-1" style={{ color: "rgba(255,255,255,0.5)" }}>commission on your customers</div></div>
            </div>
            <button data-testid="cta-start-business-btn" onClick={() => nav("/signup?role=business")}
              className="mt-8 inline-flex items-center gap-2 rounded-full font-semibold px-7 py-3.5 active:scale-[0.98]"
              style={{ background: "#fff", color: "#0F172A" }}>
              Get started as a business <ArrowRight size={17} />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
